/**
 * Transfer refresh-resume wiring — see booking-flow/BookingFlowResumer.tsx and the plan.
 * Own file so `rerunSearch` can import `searchTransfers` without a circular import.
 * NOTE: for transfer, `payment` runs BEFORE `finalize` (see flowConfig.ts header).
 */
import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { TransferShoppingCart } from '../../api/types/transfer';
import { searchTransfers } from '../../api/endpoints/transfer';
import { bookingDraftKey } from '../../booking-flow/draftStorage';
import {
  cartIsPaid,
  clampStepIndex,
  compact,
  type BookingFlowPersistConfig,
  type ResumeAdapter,
} from '../../booking-flow/resume';
import type { TransferFlowState } from './flowConfig';

/** Must match `transferFlowConfig.steps` order (asserted in resume.test.ts). */
const TRANSFER_STEP_ORDER = [
  'search',
  'allocate',
  'updatePassengers',
  'prebook',
  'payment',
  'finalize',
  'document',
] as const;
const idx = (id: string) => TRANSFER_STEP_ORDER.indexOf(id as (typeof TRANSFER_STEP_ORDER)[number]);

const FLOOR = idx('updatePassengers');
const PAYMENT = idx('payment');
const FINALIZE = idx('finalize');
const DOCUMENT = idx('document');

const transferPartialize = (s: TransferFlowState): Partial<TransferFlowState> =>
  compact({
    searchRequest: s.searchRequest?.pickupPointId ? s.searchRequest : undefined,
    selectedVehicle: s.selectedVehicle,
    passengers: s.passengers?.length ? s.passengers : undefined,
    contact: s.contact,
    billingInformation: s.billingInformation,
    paymentResponse: s.paymentResponse,
    shoppingCartId: s.shoppingCartId,
  });

export const transferPersistConfig: BookingFlowPersistConfig<TransferFlowState> = {
  key: bookingDraftKey('transfer'),
  version: 2,
  floorStepId: 'updatePassengers',
  searchResultStepId: 'allocate',
  partialize: transferPartialize,
  /** Onaya gönderilmiş taslağı YAZMA (uçuş/otel ile aynı). */
  shouldPersist: (s, stepIndex) => {
    if (s.sentForApproval) return false;
    if (stepIndex > 0) return true;
    return Object.keys(transferPartialize(s)).length > 0;
  },
};

export const transferResumeAdapter: ResumeAdapter<TransferFlowState> = {
  getCartId: (draft) => draft.shoppingCartId,
  applyResumedCart: (cart: ShoppingCart) => ({
    cart: cart as unknown as TransferShoppingCart,
    shoppingCartId: cart.id,
  }),
  resolveResumeStepId: (cart: ShoppingCart, savedStepId: string) => {
    const paid = cartIsPaid(cart);
    if (paid && cart.isFinalized) return TRANSFER_STEP_ORDER[DOCUMENT];
    if (paid) return TRANSFER_STEP_ORDER[FINALIZE]; // step after payment
    if (cart.isPriceChanged) return TRANSFER_STEP_ORDER[FLOOR];
    const saved = idx(savedStepId);
    return TRANSFER_STEP_ORDER[clampStepIndex(saved >= 0 ? saved : FLOOR, FLOOR, PAYMENT)];
  },
  rerunSearch: async (draft) => {
    if (!draft.searchRequest?.pickupPointId) return null;
    const searchResult = await searchTransfers(draft.searchRequest);
    return { searchResult };
  },
};
